'use strict';

import './component.scss';

// SVG sprites
import './svg/input.svg';
import './svg/hotel.svg';
import './svg/room.svg';
import './svg/button.svg';
import './svg/common.svg';
import './svg/workflow.svg';
import './svg/loader.svg';
import './svg/car-options.svg';
import './svg/policy-icon.svg';
import './svg/flight.svg';
import './svg/transport.svg';
import './svg/rail-seat-preferences.svg';
import './svg/route-happy.svg';

// SVG icons
import './svg/preloader.svg';

// Bing Map related
// I. Common
import './map/common/center.svg';
import './map/common/start-new-search.svg';

// II. Hotel
import './map/common/preloader.svg';
import './map/hotel/start-new-hotel-search.svg';

// III. Car
import './map/car/cluster.svg';
import './map/car/neutral.png';
import './map/car/neutral-selected.png';

// III.1 Car providers
import './map/car/providers/ZI.png'; // Avis
import './map/car/providers/ZD.png'; // Budget
import './map/car/providers/CA.png'; // Caro
import './map/car/providers/ET.png'; // Enterprise
import './map/car/providers/EP.png'; // Europcar
import './map/car/providers/ZE.png'; // Hertz
import './map/car/providers/CPK.png'; // KRONES Hire Car
import './map/car/providers/ZL.png'; // National
import './map/car/providers/SX.png'; // Sixt
import './map/car/providers/ZI-selected.png'; // Avis
import './map/car/providers/ZD-selected.png'; // Budget
import './map/car/providers/CA-selected.png'; // Caro
import './map/car/providers/ET-selected.png'; // Enterprise
import './map/car/providers/EP-selected.png'; // Europcar
import './map/car/providers/ZE-selected.png'; // Hertz
import './map/car/providers/CPK-selected.png'; // KRONES Hire Car
import './map/car/providers/ZL-selected.png'; // National
import './map/car/providers/SX-selected.png'; // Sixt
import './map/car/providers/ZI-neutral.png'; // Avis
import './map/car/providers/ZD-neutral.png'; // Budget
import './map/car/providers/CA-neutral.png'; // Caro
import './map/car/providers/ET-neutral.png'; // Enterprise
import './map/car/providers/EP-neutral.png'; // Europcar
import './map/car/providers/ZE-neutral.png'; // Hertz
import './map/car/providers/CPK-neutral.png'; // KRONES Hire Car
import './map/car/providers/ZL-neutral.png'; // National
import './map/car/providers/SX-neutral.png'; // Sixt

import { createPopper } from '@popperjs/core';

// Main selectors used to find icon tooltip triggers, tooltip elements, and the Popper arrow.
const TOOLTIP_TRIGGER_SELECTOR = '[data-icon-tooltip-trigger]';
const TOOLTIP_SELECTOR = '.icon-tooltip';
const TOOLTIP_ARROW_SELECTOR = '[data-popper-arrow]';
// Dataset property names used to track initialization and preferred placement.
const DATA_INITIALIZED = 'iconTooltipInitialized';
const DATA_POSITION = 'iconTooltipPosition';
// Shared attribute names used to show/hide the tooltip and keep it hidden from assistive technologies when closed.
const ARIA_HIDDEN = 'aria-hidden';
const DATA_SHOW = 'data-show';
// Class added to the trigger while its tooltip is visible.
const VISIBLE_CLASS = 'icon-tooltip-trigger--visible';
// Default and supported tooltip placements.
const DEFAULT_POSITION = 'bottom';
const ALLOWED_POSITIONS = ['top', 'right', 'bottom', 'left'];
// Popper spacing configuration.
const TOOLTIP_OFFSET = 8;
const ARROW_PADDING = 4;
const HIDE_DELAY = 100;

class IconTooltip {
    constructor() {
        this.tooltipId = 0;
    }

    init(container = document) {
        // Initialize all icon tooltip triggers inside the provided container.
        // The default container is the whole document.
        const tooltipTriggers = container.querySelectorAll(TOOLTIP_TRIGGER_SELECTOR);

        for (const tooltipTrigger of tooltipTriggers) {
            this.attachListeners(tooltipTrigger);
        }
    }

    attachListeners(trigger) {
        // Prevent duplicate event listeners when init() is called more than once,
        // for example after dynamically loaded content is added to the page.
        if (trigger.dataset[DATA_INITIALIZED] === 'true') {
            return;
        }

        const tooltip = trigger.querySelector(TOOLTIP_SELECTOR);

        // Do nothing if the trigger does not contain a tooltip element.
        if (!tooltip) {
            return;
        }

        trigger.dataset[DATA_INITIALIZED] = 'true';

        // Ensure the tooltip has a unique id, mainly for debugging and future extensibility.
        this.ensureTooltipId(tooltip);

        // Move the tooltip outside of the trigger.
        // This prevents layout issues caused by overflow, flex/grid,
        // transforms, or button-specific styles.
        document.body.appendChild(tooltip);

        const placement = this.normalizePlacement(trigger.dataset[DATA_POSITION]);
        const arrow = tooltip.querySelector(TOOLTIP_ARROW_SELECTOR);

        let popperInstance = null;
        let hideTimeout = null;
        let isVisible = false;

        const createPopperInstance = () => {
            // Create the Popper instance only when the tooltip is first shown.
            if (popperInstance) {
                return;
            }

            popperInstance = createPopper(trigger, tooltip, {
                placement,
                strategy: 'fixed',
                modifiers: this.getModifiers(placement, arrow),
            });
        };

        const show = () => {
            window.clearTimeout(hideTimeout);

            createPopperInstance();

            trigger.classList.add(VISIBLE_CLASS);
            tooltip.setAttribute(DATA_SHOW, '');
            tooltip.setAttribute(ARIA_HIDDEN, 'false');

            isVisible = true;

            // Recalculate the tooltip position every time it is shown.
            if (popperInstance) {
                popperInstance.update();
            }
        };

        const hide = () => {
            window.clearTimeout(hideTimeout);

            trigger.classList.remove(VISIBLE_CLASS);
            tooltip.removeAttribute(DATA_SHOW);
            tooltip.setAttribute(ARIA_HIDDEN, 'true');

            isVisible = false;

            // Destroy the Popper instance when the tooltip is hidden,
            // so it does not keep observers/listeners alive unnecessarily.
            if (popperInstance) {
                popperInstance.destroy();
                popperInstance = null;
            }
        };

        const scheduleHide = () => {
            // Small delay prevents flickering when the pointer briefly leaves the trigger.
            window.clearTimeout(hideTimeout);
            hideTimeout = window.setTimeout(hide, HIDE_DELAY);
        };

        const handleOutsidePointerDown = (event) => {
            // Hide the tooltip when clicking/tapping outside the trigger and tooltip.
            if (!isVisible || trigger.contains(event.target) || tooltip.contains(event.target)) {
                return;
            }

            hide();
        };

        // Mouse support.
        trigger.addEventListener('mouseenter', show);
        trigger.addEventListener('mouseleave', scheduleHide);

        // Keyboard support.
        trigger.addEventListener('focusin', show);
        trigger.addEventListener('focusout', scheduleHide);

        // Allow users to dismiss the tooltip with Escape.
        trigger.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') {
                hide();
            }
        });

        document.addEventListener('pointerdown', handleOutsidePointerDown);
    }

    ensureTooltipId(tooltip) {
        if (tooltip.id) {
            return;
        }

        tooltip.id = `icon-tooltip-${this.tooltipId}`;
        this.tooltipId++;
    }

    normalizePlacement(placement) {
        // Use the requested placement only if it is supported.
        if (ALLOWED_POSITIONS.indexOf(placement) > -1) {
            return placement;
        }
        return DEFAULT_POSITION;
    }

    getFallbackPlacements(placement) {
        // Define fallback order for Popper's flip modifier.
        // Example: if bottom has no space, try top, then right, then left.
        switch (placement) {
            case 'top':
                return ['bottom', 'right', 'left'];
            case 'right':
                return ['left', 'top', 'bottom'];
            case 'left':
                return ['right', 'top', 'bottom'];
            case 'bottom':
            default:
                return ['top', 'right', 'left'];
        }
    }

    getModifiers(placement, arrow) {
        // Base Popper modifiers:
        // - offset: adds spacing between trigger and tooltip
        // - flip: changes placement when there is not enough space
        // - preventOverflow: keeps the tooltip inside the viewport
        const modifiers = [
            {
                name: 'offset',
                options: {
                    offset: [0, TOOLTIP_OFFSET],
                },
            },
            {
                name: 'flip',
                options: {
                    fallbackPlacements: this.getFallbackPlacements(placement),
                    padding: TOOLTIP_OFFSET,
                },
            },
            {
                name: 'preventOverflow',
                options: {
                    padding: TOOLTIP_OFFSET,
                },
            },
        ];

        // Enable Popper arrow positioning only when an arrow element exists.
        if (arrow) {
            modifiers.push({
                name: 'arrow',
                options: {
                    element: arrow,
                    padding: ARROW_PADDING,
                },
            });
        }

        return modifiers;
    }
}

export { IconTooltip as default };